import { Button, CircularProgress } from "@mui/material";
import { Icon } from "components/Icon/Icon";
import Tooltip from "components/Tooltip/Tooltip";
import { getIsReadonly, getRecordConfig } from "config/resourceUtils";
import { getUserData, getUserPermissionData } from "containers/Auth/Auth.selectors";
import { hasCreateUserPermission } from "containers/Auth/permissions";
import { getActiveOrDefaultProjectId } from "containers/Projects/Projects.selectors";
import { getUncommittedGitLogData } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { bootstrapUserNameSelector } from "containers/Workspaces/Workspaces.selectors";
import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RouterContext } from "utils/RouterManager/RouterManager";
import { ActionData, ActionSignature } from "./Action";
import "./InlineActionButtons.scss";
interface InlineActionButtonsProps {
    children?: React.ReactNode;
    resourceData: any;
    afterButtonAction: () => void;
    hideGoToParent?: boolean;
}

export default function InlineActionButtons({
    children,
    resourceData,
    afterButtonAction,
    hideGoToParent,
}: InlineActionButtonsProps) {
    const dispatch = useDispatch();
    const history = useHistory();
    const activeProjectId = useSelector(getActiveOrDefaultProjectId);
    const userData = useSelector(getUserData);
    const permissionData = useSelector(getUserPermissionData);
    const canUserCreateUser = hasCreateUserPermission(permissionData);
    const resourceConfig = getRecordConfig(resourceData?.resource_type?.toLowerCase());
    const allUncommittedData = useContext(RouterContext).allUncommittedData;
    const uncommittedDataByPath = getUncommittedGitLogData({ allUncommittedData, resourceData });
    const bootstrapUserName = useSelector(bootstrapUserNameSelector);
    const [loadingActionKey, setLoadingActionKey] = useState<string | null>(null);

    const actions: any = resourceConfig?.actions;
    if (!actions) return null;

    return (
        <div className="inlineActionButtons">
            {actions.shown?.map((action: ActionSignature) => {
                if (!resourceData) return null;

                const actionData: ActionData = {
                    resourceData,
                    activeProjectId,
                    userData,
                    resourceType: resourceData.resource_type?.toLowerCase(),
                    isReadOnly: getIsReadonly(resourceConfig, resourceData),
                    uncommittedData: uncommittedDataByPath,
                    canUserCreateUser,
                    bootstrapUserName,
                };
                if (action.omitWhen && action.omitWhen(actionData)) return null;

                if (action.key === "goToParent") {
                    const location = window.location;
                    const isLiveMonitorPage = location.pathname.split("/")[1] === "workspace";

                    if (!isLiveMonitorPage && hideGoToParent) {
                        return null;
                    }
                }

                return (
                    <Tooltip overlay={action.label(actionData)} key={`action${action?.key}`}>
                        <Button
                            key={`action${action?.key}`}
                            aria-label={action.label(actionData)}
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                setLoadingActionKey(action.key);
                                Promise.resolve(action.action(actionData, dispatch, history))
                                    .then(afterButtonAction)
                                    .finally(() => setLoadingActionKey(null));
                            }}
                            disabled={
                                loadingActionKey !== null ||
                                action.disableWhen?.(actionData)
                            }
                        >
                            {loadingActionKey === action.key ? (
                                <CircularProgress size={19} />
                            ) : (
                                <Icon
                                    icon={
                                        action.inlineActionIcon
                                            ? action.inlineActionIcon(actionData)
                                            : action.icon(actionData)
                                    }
                                />
                            )}
                        </Button>
                    </Tooltip>
                );
            })}
            {children}
        </div>
    );
}
